import React, { useState, useEffect, useRef, useContext } from "react";
import styled from "styled-components";
import SubscriberBoxPlot from "./SubscriberBoxPlot";
import PercentageWarning from "../../../Subscriber&Groups/PercentageWarning";
import { thresholdKeyMap } from "../../../Subscriber&Groups/utils";
import ColumnsGrid from "common/layouts/ColumnsGrid";
import Switch from "@mui/material/Switch";
import Modal from "common/Modal";
import NumberInput from "common/NumberInputWithSpinnerState";

const Wrapper = styled.div`
  margin: 30px 20px 0 20px;
  padding-top: 10px;
`;

const TotalSubscribers = styled.h4`
  font-size: 16px !important;
  font-weight: 100 !important;
  color: var(--text-primary) !important;
  display: flex;
`;

const WrapperRightMenu = styled.div`
  display: flex;
  align-items: center;
`;

const WrapperCollapse = styled.div`
  display: flex;
  align-items: center;
  cursor: pointer;
  margin-left: 10px;
  color: var(--Text-Primary);
  .material-icons {
    color: var(--text-primary) !important;
    margin-left: 5px;
  }
`;

const WrapperTotalColorize = styled.div`
  display: flex;
  justify-content: space-between;
`;

const WrapperSwitch = styled.div`
  display: flex;
  align-items: center;
  color: var(--Text-Primary);
  .MuiSwitch-root {
    margin-top: -1px !important;
  }
  .sw-input.MuiSwitch-root {
    & .Mui-checked {
      color: var(--switch-checked-green);
    }

    & .Mui-checked + .MuiSwitch-track {
      background-color: var(--switch-checked-light-green);
    }
  }
`;

const CenteredMessage = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 100%;
  min-height: 25px;
  margin-bottom: 40px;
  color: var(--Text-Primary);
`;

const NoEnoughtData = () => (
  <CenteredMessage>Not enough data to display box plots.</CenteredMessage>
);

const BOXPLOT_METRICS_ORDER_MAP = {
  "MAX-Mbps": 0,
  "RTT-P80-ms": 1,
  "RTT-MED-ms": 2,
  "RTT-MIN-ms": 3,
  RTX: 4,
  "MAX-SPEED": 5,
  CONGEST: 6,
};

const specValues = new Set(["RTT-MED-ms", "RTT-MIN-ms", "RTT-P80-ms"]);

const Statistics = ({
  data,
  colorize,
  setColorize,
  thresholds,
  setThresholds,
  metricsQuantileData,
  ...settings
}) => {
  const totalSubs = data.rows ? data.rows.length : 0;
  const [showBoxplots, setShowBoxplots] = useState(true);

  const statisticsMetrics = Object.entries(data)
    .filter(([key, value]) => {
      return key !== "rows";
    })
    .sort(
      (a, b) =>
        BOXPLOT_METRICS_ORDER_MAP[a[0]] - BOXPLOT_METRICS_ORDER_MAP[b[0]],
    );

  const handleUpdateThresholds = (metric, value) => {
    const specValue = specValues.has(metric) && value !== "auto" ? value / 1000 : value;
    const cmd = `configure\npkteng\nmetrics threshold ${thresholdKeyMap[metric]} ${specValue}\ncommit\n`;
    const metricKey = metric === "RTT-P80-ms" ? "RTT-80%-ms" : metric;
    ifCl
      .run(cmd, true)
      .then(() => setThresholds({ ...thresholds, [metricKey]: value }))
      .catch((error) => showModalError("Error:", error));
  };

  return (
    <div className="card">
      <Wrapper>
        <WrapperTotalColorize>
          <TotalSubscribers>{totalSubs} active subscribers</TotalSubscribers>
          <WrapperRightMenu>
            <WrapperSwitch>
              <div>
                {!colorize
                  ? "Colorize: by warning threshold"
                  : "Colorize: by quartiles"}
              </div>
              <Switch
                className="sw-input"
                checked={colorize}
                onChange={() => setColorize(!colorize)}
              />
            </WrapperSwitch>
            <WrapperCollapse onClick={() => setShowBoxplots(!showBoxplots)}>
              {showBoxplots ? "Hide Boxplots" : "Show Boxplots"}
              <i className="material-icons icon-collapse-row">
                {showBoxplots ? "expand_less" : "expand_more"}
              </i>
            </WrapperCollapse>
          </WrapperRightMenu>
        </WrapperTotalColorize>
        <PercentageWarning
          meanThreshold={thresholds["MEAN-Mbps"]}
          percentWarning={thresholds["PERCENT-WARNING"]}
          updateThresholds={(metric, value) =>
            handleUpdateThresholds(metric, value)
          }
        />
        {!showBoxplots ? null : data.totalGroups < 2 ? (
          <NoEnoughtData />
        ) : (
          <ColumnsGrid
            columns={statisticsMetrics.length}
            minWidth="3.5cm"
            gap={"1em"}
            className="box-plot-container"
            rowGap="0"
          >
            {statisticsMetrics.map(([key, values]) => {
              return (
                <SubscriberBoxPlot
                  metric={key}
                  data={values}
                  threshold={
                    key === "RTT-P80-ms"
                      ? thresholds["RTT-80%-ms"]
                      : thresholds[key]
                  }
                  quantileData={metricsQuantileData[key]}
                  updateThresholds={handleUpdateThresholds}
                  colorize={colorize}
                  key={`box-plot-${key}`}
                />
              );
            })}
          </ColumnsGrid>
        )}
      </Wrapper>
    </div>
  );
};

export default Statistics;
